/**
 * User Role Service
 *
 * Manages the current user's portal role (Student, Lecturer, or None)
 * Provides role-based access control and role checking functionality
 *
 * @module UserRoleService
 */

import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { APP_SCHEMA_ENDPOINTS } from '../core/api';
import type { PortalRoleResponse, PortalRole } from '../core/models/contact.model';

@Injectable({
  providedIn: 'root',
})
export class UserRoleService {
  private http = inject(HttpClient);

  // LocalStorage key for role persistence
  private readonly ROLE_STORAGE_KEY = 'portal_role_data';

  // Signals for state management
  private readonly roleData = signal<PortalRoleResponse | null>(this.loadRoleFromStorage());
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  // Computed values
  readonly isStudent = computed(() => this.roleData()?.is_student ?? false);
  readonly isLecturer = computed(() => this.roleData()?.is_lecturer ?? false);
  readonly hasRole = computed(() => this.isStudent() || this.isLecturer());
  readonly primaryRole = computed(() => this.roleData()?.primary_role ?? null);
  readonly roles = computed(() => this.roleData()?.roles ?? []);
  readonly schools = computed(() => this.roleData()?.schools ?? []);

  /**
   * Get the portal role type
   * Returns 'both' if contact has both student and lecturer roles
   */
  readonly portalRole = computed<PortalRole>(() => {
    const data = this.roleData();
    if (!data) return 'none';

    // Check for dual roles first
    if (data.is_student && data.is_lecturer) return 'both';

    // Single roles
    if (data.is_student) return 'student';
    if (data.is_lecturer) return 'lecturer';

    return 'none';
  });

  /**
   * Fetch portal role for a contact
   */
  fetchPortalRole(contactId: number): Observable<PortalRoleResponse> {
    this.loading.set(true);
    this.error.set(null);

    const url = APP_SCHEMA_ENDPOINTS.contacts.portalRole(contactId);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.get<PortalRoleResponse>(url, { headers }).pipe(
      tap({
        next: (data) => {
          this.roleData.set(data);
          this.saveRoleToStorage(data);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('❌ Error fetching portal role:', error);
          this.error.set('Fehler beim Laden der Benutzerrolle');
          this.loading.set(false);
        },
      })
    );
  }

  /**
   * Load role from localStorage
   */
  private loadRoleFromStorage(): PortalRoleResponse | null {
    try {
      const stored = localStorage.getItem(this.ROLE_STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored) as PortalRoleResponse;
        return data;
      }
    } catch (error) {
      console.error('❌ Error loading role from storage:', error);
    }
    return null;
  }

  /**
   * Save role to localStorage
   */
  private saveRoleToStorage(data: PortalRoleResponse): void {
    try {
      localStorage.setItem(this.ROLE_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('❌ Error saving role to storage:', error);
    }
  }

  /**
   * Clear stored role data
   */
  clearRole(): void {
    this.roleData.set(null);
    this.error.set(null);
    localStorage.removeItem(this.ROLE_STORAGE_KEY);
    console.log('🗑️ Role cleared from storage');
  }

  /**
   * Check if role is loaded
   */
  isRoleLoaded(): boolean {
    return this.roleData() !== null;
  }

  /**
   * Check if user has a specific role
   */
  hasRoleType(role: PortalRole): boolean {
    return this.portalRole() === role;
  }

  /**
   * Get role display name
   */
  getRoleDisplayName(): string {
    const role = this.portalRole();
    switch (role) {
      case 'student':
        return 'Student';
      case 'lecturer':
        return 'Dozent';
      default:
        return 'Kein Zugriff';
    }
  }

  /**
   * Debug: Get current role state
   */
  getDebugInfo(): {
    roleData: PortalRoleResponse | null;
    isStudent: boolean;
    isLecturer: boolean;
    hasRole: boolean;
    portalRole: PortalRole;
  } {
    return {
      roleData: this.roleData(),
      isStudent: this.isStudent(),
      isLecturer: this.isLecturer(),
      hasRole: this.hasRole(),
      portalRole: this.portalRole(),
    };
  }
}
